<template>
	<div id="app">
		<Intro :content="entries" v-if="introVisible && dataFetched"/>
		<Popup v-if="visRendered && popupActive && dataFetched &&  currentWidth > 768" :snippetCurrent="snippet" :statements="this.statements"/>

		<LineStacking v-if="datasetCurrent != null && dataFetched"
			:sidebarVisible="sidebarVisible" 
			:statements="this.statements"
			:highlight="snippet.highlight"
			ref="vis"
			:snippetCurrent="snippetCurrent"
			:snippet="snippet"
			:storyActive="storyActive"
			:dataset="datasetCurrent"
			:content="entries"
			:dataTitle="dataTitle"
			:sublineYAxis="sublineYAxis"
		/>
		<SidebarStory 
			v-if="dataFetched"
			:content="entries" 
			:storyActive="storyActive"
			divid="story-sidebar-bottom" 
			pos="right"
			class="right"
		/>
		<Navigation/>
		<Progress/>
	</div>
</template>

<script>
	import { mapState } from 'vuex';
	import Intro from './components/Intro.vue'
	import SidebarStory from './components/SidebarStory.vue'
	import LineStacking from './components/LineStacking.vue'
	import Popup from './components/Popup.vue'
	import Snippets from './components/Snippets.vue'
	import Navigation from './components/Nav.vue'
	import Progress from './components/Progress.vue'

	import { select as d3Select } from 'd3';

	import axios from 'axios';

	import { content } from './assets/content';

	export default {
		name: 'app',
		components: {
			Intro,
			LineStacking,
			SidebarStory,
			Popup,
			Progress,
			Snippets,
			Navigation
		},
		data() {
			return {
				content: content,
				entries: null,
				statements: {
					year: 'year',
					lineGroup: 'externalCourseId',
					keyword: 'COMPUTER_SCIENCE',
					keywordTitle: 'Computer Science',
					startYear: 1990,
					endYear: 2010,
					searchMode: "department",
					areaGroup: 'component', // subjectDescription // component // externalCourseId
					maxCall: 10000
				}
			}
		},
		metaInfo() {
			return {
				title: content.social.title,
				meta: [
					{ charset: 'utf-8' },
					{ name: 'viewport', content: 'width=device-width, initial-scale=1'},
					{ hid: 'description', name: 'description', content: content.social.subtitle},
					{ property: 'og:url', content: content.social.urls.project},
					{ property: 'og:type', content: 'website'},
					{ property: 'og:title', content: content.social.title},
					{ property: 'og:description', content: content.social.title},
					{ property: 'og:image', content: content.social.urls.socialtag},
					{ name: 'twitter:card', content: "summary_large_image"},
					{ name: 'twitter:title', content: content.social.title},
					{ name: 'twitter:description', content: content.social.subtitle},
					{ name: 'twitter:image', content: content.social.urls.socialtag},
					{ name: 'twitter:image:alt', content: content.social.title},
				]
			}	
		},
		watch: {
			storyActive() {
				this.dispatchSnippetCount();
				this.dispatchDatasetCurrent();
			},
			snippetCurrent() {
				this.dispatchDatasetCurrent();
			},
		},
		computed: {
			...mapState(['introVisible', 'paragraphActive', 'dataFetched', 'nodesAvailable', 'sidebarVisible', 'storyActive', 'popupActive', 'storyCount', 'snippetCurrent', 'visRendered', 'storyActive', 'datasetCurrent']),
			snippet() {
				if (this.entries != null) {
					let snippet = this.entries.stories[this.storyActive].snippets[this.snippetCurrent];
					return snippet;
				}				
			},
			dataTitle() {
				if (this.entries != null && this.paragraphActive != null) {
					let title = this.entries.stories[this.storyActive].paragraphs[this.paragraphActive].dataTitle;
					return title;
				}
			},
			currentWidth() {
				return d3Select('#app').node().getBoundingClientRect().width;
			},
			sublineYAxis() {
				if (this.entries != null && this.paragraphActive != null) {
					let sublineYAxis = this.entries.stories[this.storyActive].paragraphs[this.paragraphActive].sublineYAxis
					return sublineYAxis;
				}
			},
			snippetsArr() {
				if (this.entries != null) {
					return this.entries.stories[this.storyActive].snippets;
				}
			},
		},
		methods: {
			dispatchStoryCount() {
				const count = this.entries.stories.length;
				this.$store.dispatch("SET_STORIES_COUNT", count);
			},
			dispatchSetData(val) {
				this.$store.dispatch("SET_DATA", val);
			},
			dispatchDataFetched(state) {
				this.$store.dispatch("SET_DATA_FETCHED", state);
			},
			dispatchDatasetCurrent() {
				const current = this.entries.stories[this.storyActive].data.filter(data => { return data.id == this.snippetCurrent});
				if (current[0] != undefined) {
					const url = current[0].url;
					this.$store.dispatch("SET_DATASET_CURRENT", url);
				}
			},
			dispatchSnippetCount() {
				const count = this.entries.stories[this.storyActive].snippets.length;
				this.$store.dispatch("SET_SNIPPET_COUNT", count);
			},
			fetchContent() {
				let intro, body, start;

				// fetch story-intro sheet
				axios.get(`./data/offline/data_intro.json`)
				// axios.get(`https://spreadsheets.google.com/feeds/list/1uAHt2suCPt1JMYhdG-t-uqPuxNa7QFEH-1zWiE0f2Ds/1/public/values?alt=json`)
				.then((resIntro) => {
					intro = resIntro.data.feed.entry;
					// fetch story-body sheet
					// return axios.get(`https://spreadsheets.google.com/feeds/list/1uAHt2suCPt1JMYhdG-t-uqPuxNa7QFEH-1zWiE0f2Ds/2/public/values?alt=json`);
					return axios.get(`./data/offline/data_story_body.json`);
				})
				.then((resBody) => {
					body = resBody.data.feed.entry;
					// fetch start sheet
					// return axios.get(`https://spreadsheets.google.com/feeds/list/1uAHt2suCPt1JMYhdG-t-uqPuxNa7QFEH-1zWiE0f2Ds/3/public/values?alt=json`);
					return axios.get(`./data/offline/data_start.json`);
				})
				.then((resStart) => {
						start = resStart.data.feed.entry;

						let obj = {
							stories: [],
							legend: null,
							intro: null,
							start: null
						}

						let datasetCurrent = "";
						let datasetTitleCurrent = "";
						let newDatasetCurrent = 'FALSE';
						let sublineYAxisCurrent = ''

						start.forEach((storyStart, index) => {
							const title = storyStart['gsx$title']['$t'];
							const subtitle = storyStart['gsx$subtitle']['$t'];
							const p1 = storyStart['gsx$p1']['$t'];

							obj.start = {
								title: title,
								subtitle: subtitle,
								p1: p1
							}
						})

						intro.forEach((storyIntro, index) => {
							const storyId = storyIntro['gsx$story-id']['$t'];
							const authors = storyIntro['gsx$authors']['$t'].split(',');
							const introTitle = storyIntro['gsx$intro-title']['$t'];
							const introSubtitle = storyIntro['gsx$intro-subtitle']['$t'];
							const authorLinks = storyIntro['gsx$authors-links']['$t'].split(',');

							let authorsArr = [];

							authors.forEach((author,i) => {
								authorsArr.push([author, authorLinks[i]])
							})

							obj.stories.push({
								id: storyId,
								authors: authorsArr,
								introduction: {
									title: introTitle,
									subtitle: introSubtitle
								},
								data: [],
								paragraphs: [],
								snippets: []
							});
							

							// all items matching current story id
							const storyItems = body.filter(body => { return body['gsx$story-id']['$t'] == storyId });
							
							storyItems.forEach((item,i) => {
								
								if (item['gsx$subline-y-axis'] != undefined) {
									if (item['gsx$subline-y-axis']['$t'] != '' && item['gsx$subline-y-axis']['$t'] != sublineYAxisCurrent) {
										sublineYAxisCurrent = item['gsx$subline-y-axis']['$t'];
									}
								}

								if (item['gsx$data-title'] != undefined) {
									if (item['gsx$data-title']['$t'] != '' && item['gsx$data-title']['$t'] != datasetTitleCurrent) {
										datasetTitleCurrent = item['gsx$data-title']['$t'];
										newDatasetCurrent = 'TRUE';
									} else {
										newDatasetCurrent = 'FALSE';
									}
								} 

								obj.stories[storyId].paragraphs.push({
									id: i,
									pId: i,
									dataTitle: datasetTitleCurrent,
									refId: item['gsx$story-id']['$t'],
									snippetText: item['gsx$snippet-text']['$t'],
									uniqueId: `${Number(i)}-${Number(i)}`,
									title: item['gsx$paragraph-title']['$t'],
									text: item['gsx$paragraph-text']['$t'],
									imageLink: item['gsx$image-link']['$t'],
									audioLink: item['gsx$audio-link']['$t'],
									videoLink: item['gsx$video-link']['$t'],
									imageSubline: item['gsx$image-subline']['$t'],
									audioSubline: item['gsx$audio-subline']['$t'],
									videoSubline: item['gsx$video-subline']['$t'],
									newDataset: newDatasetCurrent,
									sublineYAxis: sublineYAxisCurrent,
									highlighted: item['gsx$snippet-highlight']['$t'].length > 0 ? 'TRUE' : 'FALSE'
								})
							})

							storyItems.forEach((item,i) => {
								
								if (item['gsx$data-link']['$t'] != '' && item['gsx$data-link']['$t'] != datasetCurrent) {
									datasetCurrent = item['gsx$data-link']['$t'];
								}

								obj.stories[storyId].data.push({
									id: Number(i),
									url: datasetCurrent,
								})
							})

							storyItems.forEach((item,i) => {
								obj.stories[storyId].snippets.push({
									pId: Number(i),
									snippetId: Number(i),
									highlight: item['gsx$snippet-highlight']['$t'].length > 0 ? item['gsx$snippet-highlight']['$t'].split(",").map(Number) : '',
									pos: item['gsx$snippet-pos']['$t'].split(",").map(Number),
									title: item['gsx$snippet-title']['$t'],
									text: item['gsx$snippet-text']['$t'],
								})

								
							})
						})

						obj.legend = this.content.legend;
						obj.intro = intro;
						obj.social = this.content.social;

						this.entries = obj;
						
						this.dispatchStoryCount();
						this.dispatchSnippetCount();
						this.dispatchDatasetCurrent();

						this.dispatchSetData(obj);
						this.dispatchDataFetched(true);
					})
			}
		},
		created() {
			this.fetchContent();
		}
	}
</script>

<style lang="scss">
	@import './assets/style/main.scss'; 

	body {
		margin: 0;
		overflow: hidden;
	}

	#app {
		font-family: 'IBM Plex Sans';
		color: $color-primary;
		width: 100%;
		height: 100%;
		position: absolute;
		display: flex;
		flex-direction: row;
		// display: flex;
		// flex-direction: row;
		// align-items: stretch;
	}
</style>
